/**
 * Helpers for mapping workflow steps to timeline annotations.
 *
 * The timeline may be missing `step-completed` markers for failed or in-flight steps,
 * so UI code must match by node identity and occurrence order instead of raw array index.
 *
 * @module ui/components/workflowStepTimeline
 */

import type { VideoTimeline, VideoTimelineEntry } from '@chatsdet/types';

/** Minimal step shape the anchor matcher needs (only the node identity). */
type NodeIdentifiedStep = { nodeId: string };

type StepTimelineAnchor = {
  primary: VideoTimelineEntry | null;
  started: VideoTimelineEntry | null;
  completed: VideoTimelineEntry | null;
};

function getAnnotationsByType(
  timeline: VideoTimeline | null,
  annotationType: 'step-started' | 'step-completed'
): Map<string, VideoTimelineEntry[]> {
  const grouped = new Map<string, VideoTimelineEntry[]>();

  for (const entry of timeline?.entries ?? []) {
    if (!entry.isAnnotation || entry.annotationType !== annotationType || !entry.nodeId) {
      continue;
    }

    const entries = grouped.get(entry.nodeId) ?? [];
    entries.push(entry);
    grouped.set(entry.nodeId, entries);
  }

  return grouped;
}

export function matchStepTimelineAnchors(
  steps: ReadonlyArray<NodeIdentifiedStep>,
  timeline: VideoTimeline | null
): StepTimelineAnchor[] {
  const startedByNode = getAnnotationsByType(timeline, 'step-started');
  const completedByNode = getAnnotationsByType(timeline, 'step-completed');
  const occurrenceByNode = new Map<string, number>();

  return steps.map((step) => {
    const occurrence = occurrenceByNode.get(step.nodeId) ?? 0;
    occurrenceByNode.set(step.nodeId, occurrence + 1);

    const started = startedByNode.get(step.nodeId)?.[occurrence] ?? null;
    const completed = completedByNode.get(step.nodeId)?.[occurrence] ?? null;

    return {
      primary: completed ?? started,
      started,
      completed,
    };
  });
}

export function findActiveStepIndexFromAnchors(
  anchors: StepTimelineAnchor[],
  currentTimeMs: number
): number {
  let activeIndex = -1;

  for (let i = 0; i < anchors.length; i++) {
    const timestampMs = anchors[i]?.primary?.timestampMs;
    if (timestampMs === undefined || timestampMs === null) {
      continue;
    }

    if (timestampMs <= currentTimeMs) {
      activeIndex = i;
    } else {
      break;
    }
  }

  return activeIndex;
}
